import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@decisiv/ui-components';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';

import { CascadeSection } from './style';
import SyncStatusTable from './SyncStatusTable';
import { cascadeStatusTag } from './syncStatus';

// Reports what a Shared Settings lifecycle cascade did across the group, so the
// automatic behaviour is not a black box. Every peer the API reported gets a row,
// including the ones it deliberately left alone — a silently dropped status reads
// as "nothing happened", which is what this is here to prevent.
const copyFor = (action) =>
  ({
    location_add: {
      title: <Trans>Also added to linked integrations</Trans>,
      lead: (
        <Trans>
          This location is linked through a Shared Setting, so it was added to
          the other integrations in the group.
        </Trans>
      ),
    },
    location_enable: {
      title: <Trans>Also enabled in linked integrations</Trans>,
      lead: (
        <Trans>
          This location is linked through a Shared Setting, so enabling it here
          also applied to the other integrations in the group.
        </Trans>
      ),
    },
    location_disable: {
      title: <Trans>Also disabled in linked integrations</Trans>,
      lead: (
        <Trans>
          This location is linked through a Shared Setting, so disabling it here
          also applied to the other integrations in the group.
        </Trans>
      ),
    },
    location_removal: {
      title: <Trans>Also removed from linked integrations</Trans>,
      lead: (
        <Trans>
          This location is linked through a Shared Setting, so removing it here
          also applied to the other integrations in the group.
        </Trans>
      ),
    },
  }[action] ?? {
    title: <Trans>Shared Setting sync</Trans>,
    lead: (
      <Trans>
        This location is linked through a Shared Setting. Here is what the
        change did to the other integrations in the group.
      </Trans>
    ),
  });

export default function CascadeSummary({ action = null, results, onDismiss }) {
  if (results.length === 0) return null;

  const { title, lead } = copyFor(action);
  const rows = results.map((result) => ({
    id: result.integration_uuid,
    name: result.integration_name,
    ...cascadeStatusTag(result),
    attention: result.missing_attributes?.length
      ? result.missing_attributes.join(', ')
      : result.detail,
  }));

  return (
    <CascadeSection data-testid="cascade-summary">
      <H3 marginBottom={0.5}>{title}</H3>
      <P size="small" shade={1} marginBottom={1}>
        {lead}
      </P>

      <SyncStatusTable rows={rows} attentionTitle={t`Needs attention`} />

      <Flex marginTop={1}>
        <Button
          text={t`Dismiss`}
          kind="secondary"
          size="small"
          onClick={onDismiss}
        />
      </Flex>
    </CascadeSection>
  );
}
CascadeSummary.propTypes = {
  action: PropTypes.string,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      integration_uuid: PropTypes.string,
      integration_name: PropTypes.string,
      status: PropTypes.string,
      enabled: PropTypes.bool,
      missing_attributes: PropTypes.arrayOf(PropTypes.string),
      detail: PropTypes.string,
    }),
  ).isRequired,
  onDismiss: PropTypes.func.isRequired,
};
